import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone } from '@angular/core';
import { RendererFactory2, RendererStyleFlags2, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { DeviceService, WINDOW } from '@frontend/vanilla/core';
import { map } from 'rxjs';
import { fromEvent, merge } from 'rxjs';

import { ModalRef } from '../../common/modal-ref';
import { OpenedModalsStorageService } from '../opened-modals-storage.service';
import { ModalGestureQueriesService } from './modal-gesture-queries.service';
import {
    CLOSING_VELOCITY_HORIZONTAL,
    CLOSING_VELOCITY_VERTICAL,
    EXCLUDED_FROM_GESTURE_SELECTOR,
    HORIZONTAL_SCROLL_CONTAINER_SELECTOR,
} from './modal-gesture.constants';
import { Background } from './modal-gesture.model';

@Injectable({ providedIn: 'root' })
export class ModalGestureHelperService {
    readonly #window = inject(WINDOW);
    readonly #document = inject(DOCUMENT);
    readonly #ngZone = inject(NgZone);
    readonly #modalGestureQueries = inject(ModalGestureQueriesService);
    readonly #openedModalsStorageService = inject(OpenedModalsStorageService);
    readonly #renderer = inject(RendererFactory2).createRenderer(null, null);
    readonly #deviceService = inject(DeviceService);

    get screenWidth(): number {
        return this.#window.screen.width;
    }
    get screenHeight(): number {
        return this.#window.screen.height;
    }

    get viewportWidth(): number {
        return this.viewport().width;
    }

    get viewportHeight(): number {
        return this.viewport().height;
    }

    readonly viewport = toSignal(
        merge(this.#deviceService.orientation, fromEvent(this.#window, 'resize')).pipe(
            map(() => ({
                width: this.#window.innerWidth,
                height: this.#window.innerHeight,
            })),
        ),
        {
            initialValue: {
                width: this.#window.innerWidth,
                height: this.#window.innerHeight,
            },
        },
    );

    getActiveModal(): ModalRef | undefined {
        return this.#openedModalsStorageService.activeModal;
    }

    isLastVerticalModal(): boolean {
        return this.#modalGestureQueries.getActiveModalWindowsExcludingCurrent().filter((modal) => !this.isEventDetailsModal(modal)).length === 0;
    }

    isEventDetailsModal(modal: HTMLElement): boolean {
        return modal.classList.contains('event-details-modal');
    }

    blockScrolling(element: HTMLElement | null): void {
        if (element) {
            this.#renderer.setStyle(element, 'overflow-x', `hidden`, RendererStyleFlags2.Important);
            this.#renderer.setStyle(element, 'overflow-y', `hidden`, RendererStyleFlags2.Important);
        }
    }

    unblockScrolling(element: HTMLElement | null): void {
        if (element) {
            this.#renderer.removeStyle(element, 'overflow-x');
            this.#renderer.removeStyle(element, 'overflow-y');
        }
    }

    isHorizontallyScrollable(element: EventTarget | null): boolean {
        if (!(element instanceof HTMLElement || element instanceof SVGElement)) {
            return false;
        }

        const scrollContainer = element.closest(HORIZONTAL_SCROLL_CONTAINER_SELECTOR);

        return Boolean(scrollContainer && scrollContainer.scrollWidth > scrollContainer.clientWidth);
    }

    isExcludedFromGesture(element: EventTarget | null): boolean {
        if (!(element instanceof HTMLElement || element instanceof SVGElement)) {
            return false;
        }

        const isElementOrAncestorExcluded = element.closest(EXCLUDED_FROM_GESTURE_SELECTOR);

        return Boolean(isElementOrAncestorExcluded);
    }

    disableBackgroundTransitions(background: Background): void {
        if (background?.elements) {
            background.elements.forEach((element) => {
                this.#renderer.setStyle(element, 'transition', 'none');
            });
        }
    }

    enableBackgroundTransitions(background: Background): void {
        this.#ngZone.runOutsideAngular(() => {
            this.#window.setTimeout(() => {
                if (background?.elements) {
                    background.elements.forEach((element) => {
                        this.#renderer.removeStyle(element, 'transition');
                    });
                }
            });
        });
    }

    disableConflictingBackgroundAnimations(background: Background): void {
        this.#renderer.addClass(this.#document.body, 'disable-fast-svg-placeholder-animation');

        if (background?.elements) {
            background.elements.forEach((element) => {
                this.#renderer.addClass(element, 'disable-option-value-animation');
            });
        }
    }

    enableConflictingBackgroundAnimations(background: Background): void {
        this.#renderer.removeClass(this.#document.body, 'disable-fast-svg-placeholder-animation');

        this.#ngZone.runOutsideAngular(() => {
            this.#window.setTimeout(() => {
                if (background?.elements) {
                    background.elements.forEach((element) => {
                        this.#renderer.removeClass(element, 'disable-option-value-animation');
                    });
                }
            });
        });
    }

    cancelBackgroundAnimations(background: Background): void {
        if (background?.elements) {
            background.elements.forEach((element) => {
                element.getAnimations().forEach((animation) => {
                    animation.cancel();
                });
            });
        }
    }

    setBackdropTransparent(): void {
        const backdrop = this.#modalGestureQueries.getModalBackdrop();

        if (backdrop) {
            this.#renderer.setStyle(backdrop, 'opacity', '0');
        }
    }

    setEventBackdropOpaque(): void {
        const edpModalBackdrop = this.#modalGestureQueries.getEventDetailsModalBackdrop();

        if (edpModalBackdrop) {
            this.#renderer.setStyle(edpModalBackdrop, 'opacity', '1');
        }
    }

    setEventBackdropTransparent(): void {
        const edpModalBackdrop = this.#modalGestureQueries.getEventDetailsModalBackdrop();

        if (edpModalBackdrop) {
            this.#renderer.setStyle(edpModalBackdrop, 'opacity', '0');
        }
    }

    getHammerInput(event: HammerInput | TouchEvent, dimension: 'horizontal' | 'vertical'): { deltaX: number; deltaY: number; velocity: number } {
        if (this.isHammerInput(event)) {
            return event;
        }

        return this.getTouchEventInput(event, dimension);
    }

    isHammerInput(event: HammerInput | TouchEvent): event is HammerInput {
        return event && typeof (event as HammerInput)?.velocity === 'number';
    }

    /**
     * If panend wasn't received (e.g. because children called stopPropagation on touchend),
     * we receive our capturing touchend fallback event instead, in which case we take the coordinates
     * from the native event and assume the velocity is approximately the closing velocity, since this
     * usually happens when 'swiperight' is triggered on a child and the velocity is reasonably high
     * */
    getTouchEventInput(event: TouchEvent, dimension: 'horizontal' | 'vertical'): { deltaX: number; deltaY: number; velocity: number } {
        const x = event.changedTouches?.[0]?.clientX ?? 0;
        const y = event.changedTouches?.[0]?.clientY ?? 0;

        const closingVelocity = dimension === 'horizontal' ? CLOSING_VELOCITY_HORIZONTAL : CLOSING_VELOCITY_VERTICAL;

        return { deltaX: x, deltaY: y, velocity: closingVelocity };
    }

    /**
     * Hack to reduce 'white corners' showing around the betslip during animations (legacy z-index stacking context issue)
     * For the animations where the issue is most visible, remove the top-left (where this is most visible) border radius
     * */
    setBetslipBarBorderRadius(type: 'flat' | 'rounded', betslipBar?: HTMLElement | null): void {
        const _betslipBar = betslipBar ?? this.#modalGestureQueries.getBetslipBar();

        const borderRadius = type === 'flat' ? '0px 6px 0px 0px' : '6px 6px 0px 0px';

        if (_betslipBar) {
            this.#renderer.setStyle(_betslipBar, 'border-radius', borderRadius);
        }
    }

    setPreviousUrl(urlHistory?: string[]): string | undefined {
        const nonEdpUrls = urlHistory?.filter((url) => !url.includes('/events'));

        if (nonEdpUrls && nonEdpUrls.length !== 0) {
            return nonEdpUrls[nonEdpUrls.length - 1];
        }

        return undefined;
    }
}
